import LoadingButton from '@/src/app/components/inputs/LoadingButton';
import ConfirmationDialog from '@/src/app/components/messages/ConfirmationDialog';
import ErrorToast, {
  showToast,
} from '@/src/app/components/messages/ErrorAlert';
import { ApiError } from '@/src/app/Interfaces/system/Generic.interfaces';
import { postUserRequest } from '@/src/app/services/system/PreferencesService';
import { useMutation } from '@tanstack/react-query';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import React, { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FormControlledInputArea from '../../../../components/inputs/FormInputs/FormControlledInputArea';

export type RequestInfo = {
  content: string;
};

const MAX_CHARS = 300;

const Requests = () => {
  const formMethods = useForm<RequestInfo>();
  const { handleSubmit, watch, resetField } = formMethods;
  const charsLeft = MAX_CHARS - (watch('content') ?? '').length;
  const toast = useRef<Toast>(null);

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: RequestInfo) => {
      return postUserRequest(data.content);
    },
    onError: (error: ApiError) => {
      showToast(
        'error',
        toast,
        'Create Contract Error',
        error.response?.data?.message ||
          'An error occurred! please try again later',
        3000
      );
    },
    onSuccess: () => {
      resetField('content');
      showToast(
        'success',
        toast,
        'Request Success',
        'The request was sent to the admins successfully',
        3000
      );
    },
  });

  const onSubmit = (data: RequestInfo) => {
    mutate(data);
  };

  return (
    <FormProvider {...formMethods}>
      <ErrorToast toastRef={toast} />
      <Tooltip target=".requests-info-icon" />
      <div className="font-bold text-22px mb-2 flex gap-2 align-items-center">
        Requests
        <i
          className="requests-info-icon text-18px pi pi-info-circle blackText font-bold"
          data-pr-tooltip="Use this section to send a request to the system admins. Requests are reviewed and responded to by the admin team."
          data-pr-position="right"
        />
      </div>
      <FormControlledInputArea
        defaultValue=""
        formID="content"
        rows={5}
        cols={60}
        maxLength={MAX_CHARS}
        rules={{
          required: {
            value: true,
            message: 'Request Content is a required field',
          },
        }}
      />
      <div
        className={`text-left text-sm mt-1 ${
          charsLeft <= 50 ? 'text-red-500' : 'text-color-secondary'
        }`}
      >
        {charsLeft} characters remaining
      </div>
      <div className="mt-2">
        <ConfirmationDialog
          Button={
            <LoadingButton
              isLoading={isLoading}
              label="Submit"
              className="w-8rem bluwaiBlue border-0 flex ml-auto py-3"
              fontSize="text-20px"
              type="button"
            />
          }
          onConfirm={handleSubmit(onSubmit)}
          message="Are you sure you want to submit this request?"
        />
      </div>
    </FormProvider>
  );
};

export default Requests;
